import {
  DynamicDrawUsage,
  InstancedMesh,
  Matrix4,
  Quaternion,
  Vector3,
  type BufferGeometry,
} from 'three';
import cart from '../../data/cart.generated.json';
import type { WorldFeature } from '../types';
import { createContactShadows } from '../../render/contactShadows';
import { SURFACE } from '../mesh/attributes';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { hexToLinear } from '../../core/color';
import { buildHorseModel } from './models';
import { buildOxModel, buildDraftCart, buildHarness, buildMountedRider } from './draftModels';
import { box } from './lifeGeometry';
import { quadrupedLegMatrix } from './quadrupedGait';
import { cargoGround } from './cargoGround';
import { cargoMotionAt, cargoStyle } from './cargoMotion';
import { CARGO } from './cargoPlan';
import { DRAFT, draftAt, planDraftRoutes } from './draftPlan';
import { lineOf, type OnLine } from './crowd/walkLine';
import { streetAssetGeometry, streetInstances } from './streetAsset';

export const draftTrafficFeature: WorldFeature = {
  id: 'draft-traffic',
  label: 'Draft animals and mounted riders',
  weight: 0.25,
  build(ctx) {
    const crowd = ctx.lifePlan?.crowd;
    if (!crowd) return { objects: [], animated: [], obstacles: [], counts: {} };
    const routes =
      ctx.lifePlan?.draftRoutes ??
      planDraftRoutes(crowd, cargoGround(ctx.layout, ctx.site, ctx.lots));
    if (!routes.length) return { objects: [], animated: [], obstacles: [], counts: {} };
    const material = ctx.materials.painted({ instanced: true });
    const meshes: InstancedMesh[] = [];
    const make = (name: string, geometry: BufferGeometry, capacity: number): InstancedMesh => {
      streetInstances(geometry, capacity, 2100 + meshes.length * 20);
      const mesh = new InstancedMesh(geometry, material, capacity);
      mesh.name = name;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      meshes.push(mesh);
      return mesh;
    };
    const animals = Object.fromEntries(
      (['horse', 'ox'] as const).map((kind) => {
        const ox = kind === 'ox';
        const count = routes.reduce(
          (n, r) => n + (r.kind === 'ox-cart' ? (ox ? 2 : 0) : ox ? 0 : 1),
          0
        );
        const model = ox ? buildOxModel(ctx.palette) : buildHorseModel(ctx.palette);
        return [
          kind,
          {
            model,
            body: make(`draft-${kind}-body`, model.body, count),
            fore: make(`draft-${kind}-forelegs`, model.arm, count * 2),
            hind: make(`draft-${kind}-hindlegs`, model.leg, count * 2),
          },
        ];
      })
    );
    const carts = routes.filter((r) => r.kind.endsWith('-cart'));
    const bodies = make('draft-cart-bodies', buildDraftCart(), carts.length);
    const wheels = make(
      'draft-cart-wheels',
      streetAssetGeometry(cart.wheel, SURFACE.wood).scale(
        DRAFT.wheelRadiusM / CARGO.wheelRadius,
        DRAFT.wheelRadiusM / CARGO.wheelRadius,
        1
      ),
      carts.length * 2
    );
    const loads = make(
      'draft-cart-loads',
      streetAssetGeometry(cart.barrels, SURFACE.wood)
        .translate(-CARGO.axleX, 0.1, 0)
        .scale(1.6, 1, 2.4),
      carts.length
    );
    const horseHarness = make(
      'draft-horse-harness',
      buildHarness(false),
      routes.filter((r) => r.kind === 'horse-cart').length
    );
    const oxHarness = make(
      'draft-ox-yokes',
      buildHarness(true),
      routes.filter((r) => r.kind === 'ox-cart').length
    );
    const riders = make(
      'mounted-travellers',
      buildMountedRider(false),
      routes.filter((r) => r.kind === 'rider').length
    );
    const escorts = make(
      'mounted-escorts',
      buildMountedRider(true),
      routes.filter((r) => r.kind === 'escort').length
    );
    const poleBuilder = createMeshBuilder();
    box(poleBuilder, hexToLinear('#77583a'), [-0.035, 0, -0.035], [0.035, 1, 0.035]);
    const poles = make(
      'draft-cart-shafts',
      poleBuilder.finish(),
      horseHarness.count * 2 + oxHarness.count
    );
    const shadows = createContactShadows(
      animals['horse']!.body.count + animals['ox']!.body.count + carts.length,
      material
    );
    const actors = routes.map((route, i) => ({
      route,
      line: lineOf(route.points.map(([x, y]) => [x, y, ctx.site.queries.groundHeightAt([x, y])])),
      style: {
        ...cargoStyle(i),
        speed: route.kind === 'ox-cart' ? 0.6 : route.kind.endsWith('-cart') ? 0.85 : 1.15,
        pauseS: route.kind.endsWith('-cart') ? 7 : 0,
      },
    }));
    const base = new Matrix4(),
      animalBase = new Matrix4(),
      trailer = new Matrix4(),
      local = new Matrix4();
    const a: OnLine = { x: 0, y: 0, ground: 0, heading: 0 },
      b = { ...a };
    const start = new Vector3(),
      end = new Vector3(),
      direction = new Vector3(),
      scale = new Vector3(),
      up = new Vector3(0, 1, 0);
    const rotation = new Quaternion();
    let day = true;
    const animated = {
      id: 'draft-traffic-motion',
      setTimeOfDay(_minutes: number, activity: number) {
        day = activity > 0.05;
      },
      update(seconds: number) {
        let horseIndex = 0,
          oxIndex = 0,
          cartIndex = 0,
          horseHarnessIndex = 0,
          oxHarnessIndex = 0,
          riderIndex = 0,
          escortIndex = 0,
          poleIndex = 0;
        const horseKinds: string[] = [],
          oxKinds: string[] = [];
        shadows.begin();
        for (const actor of actors) {
          const { route, line, style } = actor;
          const motion = cargoMotionAt(line.length, style, seconds);
          draftAt(line, motion.distance, a);
          base
            .makeRotationY(a.heading)
            .scale(scale.setScalar(day ? 1 : 0))
            .setPosition(a.x, a.ground, -a.y);
          const ox = route.kind === 'ox-cart';
          const animal = animals[ox ? 'ox' : 'horse']!;
          for (const offset of ox ? [-0.56, 0.56] : [0]) {
            const index = ox ? oxIndex++ : horseIndex++;
            (ox ? oxKinds : horseKinds).push(route.kind);
            animalBase.makeTranslation(0, 0, offset).premultiply(base);
            animal.body.setMatrixAt(index, animalBase);
            if (day) shadows.add(animalBase, 1.2, 0.43);
            for (const [fore, mesh] of [
              [true, animal.fore],
              [false, animal.hind],
            ] as const)
              for (const side of [-1, 1] as const) {
                quadrupedLegMatrix(
                  local,
                  animal.model,
                  motion.distance,
                  motion.speed / style.speed,
                  fore,
                  side
                );
                mesh.setMatrixAt(index * 2 + (side + 1) / 2, local.premultiply(animalBase));
              }
          }
          if (route.kind === 'rider' || route.kind === 'escort') {
            local
              .makeTranslation(
                0,
                0.015 * Math.sin((motion.distance * Math.PI * 4) / animal.model.strideM),
                0
              )
              .premultiply(base);
            if (route.kind === 'rider') riders.setMatrixAt(riderIndex++, local);
            else escorts.setMatrixAt(escortIndex++, local);
            continue;
          }
          if (ox) oxHarness.setMatrixAt(oxHarnessIndex++, base);
          else horseHarness.setMatrixAt(horseHarnessIndex++, base);
          draftAt(line, motion.distance - DRAFT.trailerM, b);
          trailer
            .makeRotationY(b.heading)
            .scale(scale.setScalar(day ? 1 : 0))
            .setPosition(b.x, b.ground, -b.y);
          bodies.setMatrixAt(cartIndex, trailer);
          loads.setMatrixAt(cartIndex, trailer);
          if (day) shadows.add(trailer, 1.3, 0.9);
          for (const side of [-1, 1]) {
            local
              .makeRotationZ(-motion.distance / DRAFT.wheelRadiusM)
              .setPosition(0, DRAFT.wheelRadiusM, side * 0.95)
              .premultiply(trailer);
            wheels.setMatrixAt(cartIndex * 2 + (side + 1) / 2, local);
          }
          for (const offset of ox ? [0] : [-0.5, 0.5]) {
            start.set(1.1, 0.89, offset).applyMatrix4(trailer);
            end.set(0.65, ox ? 1.42 : 1.24, offset).applyMatrix4(base);
            direction.copy(end).sub(start);
            const length = direction.length();
            rotation.setFromUnitVectors(up, length > 0 ? direction.divideScalar(length) : up);
            local.compose(start, rotation, scale.set(day ? 1 : 0, day ? length : 0, day ? 1 : 0));
            poles.setMatrixAt(poleIndex++, local);
          }
          cartIndex++;
        }
        animals['horse']!.body.userData['draftKinds'] = horseKinds;
        animals['ox']!.body.userData['draftKinds'] = oxKinds;
        for (const mesh of meshes) mesh.instanceMatrix.needsUpdate = true;
        shadows.end();
      },
    };
    animated.update(0);
    return {
      objects: [...meshes, shadows.root],
      animated: [animated],
      obstacles: [],
      counts: {
        draftCarts: carts.length,
        draftHorses: animals['horse']!.body.count,
        draftOxen: animals['ox']!.body.count,
        mountedRiders: riders.count + escorts.count,
      },
    };
  },
};
